import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { listOpenThemes } from "../../http/themes";
import routePaths from "../../router/route-paths";

const formatWindow = (theme) => {
  if (!theme?.startDate || !theme?.endDate) return "Dates TBD";
  return `${dayjs(theme.startDate).format("ddd, MMM D · h:mm A")} – ${dayjs(
    theme.endDate
  ).format("ddd, MMM D, YYYY · h:mm A")}`;
};

/**
 * Active and upcoming themes, for karaoke uploaders who cannot open the
 * Themes page. Shows what the songs are being added for and opens the
 * upload wizard under a theme, so its dates and stickers are filled in.
 */
const UpcomingThemesCard = () => {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listOpenThemes();
        if (!cancelled) setThemes(list);
      } catch (err) {
        console.error("Error loading upcoming themes:", err);
        if (!cancelled) {
          setError(err?.response?.data?.message || "Could not load themes.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addSongForTheme = (themeId) => {
    navigate(`${routePaths.uploadKaraoke}?themeId=${encodeURIComponent(themeId)}`);
  };

  return (
    <Box
      sx={{
        padding: "15px",
        borderRadius: "6px",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#000" }}>
        Upcoming themes
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
        Songs added for a theme take its start and end dates automatically.
        Use “Add song” next to a theme, or pick the theme inside the upload
        form.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {!loading && error && (
        <Typography sx={{ fontSize: "13px", color: "#b3261e", mt: 1.5 }}>
          {error}
        </Typography>
      )}
      {!loading && !error && themes.length === 0 && (
        <Typography sx={{ fontSize: "13px", color: "#666", mt: 1.5 }}>
          No active or upcoming themes right now.
        </Typography>
      )}
      {!loading && themes.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", mt: 1.5 }}>
          {themes.map((theme) => {
            const active = theme.status === "active";
            const songCount = Array.isArray(theme.karaokes)
              ? theme.karaokes.length
              : Array.isArray(theme.karaokeIds)
              ? theme.karaokeIds.length
              : 0;
            return (
              <Box
                key={theme._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                  padding: "10px 0",
                  borderTop: "1px solid rgba(224, 224, 224, 1)",
                }}
              >
                <Chip
                  label={active ? "Active now" : "Upcoming"}
                  color={active ? "success" : "warning"}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "11px" }}
                />
                <Box sx={{ flex: 1, minWidth: "200px" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#000" }}>
                    {theme.title}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#666" }}>
                    {formatWindow(theme)}
                    {` · ${songCount} song${songCount === 1 ? "" : "s"} so far`}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => addSongForTheme(theme._id)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#142C5C",
                    color: "#142C5C",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#0f2247",
                      backgroundColor: "rgba(20, 44, 92, 0.04)",
                    },
                  }}
                >
                  Add song
                </Button>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default UpcomingThemesCard;
